import { Fzf, byLengthAsc, extendedMatch } from 'fzf'
import type { ServiceArtefact, ServiceRun, ServiceRunStatus, ServiceRunTrigger } from '~/types/report-service'
import { definitionRoute } from '~/utils/reports/definition-listing'

const DAY_MS = 24 * 60 * 60 * 1000

const ACTIVE_STATUSES: ServiceRunStatus[] = ['queued', 'running']

export const DOWNLOAD_RECENT_DAYS = 7

export const DOWNLOAD_ARCHIVE_DAYS = 30

export const DOWNLOAD_EXPIRY_URGENT_DAYS = 2

export const DOWNLOAD_EXPIRY_SOON_DAYS = 7

export const ALL_DOWNLOADS_FILTER = 'all'

export const REPORT_DOWNLOADS_RUN_LIMIT = 100

export interface DownloadBuckets {
  active: ServiceRun[]
  recent: ServiceRun[]
  archive: ServiceRun[]
  olderCount: number
}

export interface DownloadRow {
  key: string
  run: ServiceRun
  artefact: ServiceArtefact | null
  name: string
  requestedAt: string
  status: ServiceRunStatus
  trigger: ServiceRunTrigger
  format: string | null
  sizeBytes: number | null
  rowCount: number | null
  expiresAt: string | null
  truncated: boolean
  isReady: boolean
}

export type DownloadExpiryTone = 'none' | 'expired' | 'urgent' | 'soon' | 'normal'

export type DownloadExpiryFilter = { kind: 'all' } | { kind: 'expired' } | { kind: 'within'; days: number }

export interface DownloadRowFilters {
  search?: string
  format?: string | null
  status?: string | null
  trigger?: string | null
  expiry?: DownloadExpiryFilter
}

const toTime = (value: string | null | undefined): number | null => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

const byRequestedAtDesc = (left: ServiceRun, right: ServiceRun): number =>
  right.requestedAt.localeCompare(left.requestedAt)

export const isDownloadRunActive = (run: ServiceRun): boolean => ACTIVE_STATUSES.includes(run.status)

export const runAgeInDays = (run: ServiceRun, now: Date): number => {
  const requested = toTime(run.requestedAt)
  if (requested === null) return 0
  return (now.getTime() - requested) / DAY_MS
}

export const bucketRunsByRecency = (runs: ServiceRun[] | null | undefined, now: Date): DownloadBuckets => {
  const buckets: DownloadBuckets = { active: [], recent: [], archive: [], olderCount: 0 }

  for (const run of runs ?? []) {
    if (isDownloadRunActive(run)) {
      buckets.active.push(run)
      continue
    }
    const age = runAgeInDays(run, now)
    if (age <= DOWNLOAD_RECENT_DAYS) buckets.recent.push(run)
    else if (age <= DOWNLOAD_ARCHIVE_DAYS) buckets.archive.push(run)
    else buckets.olderCount += 1
  }

  buckets.active.sort(byRequestedAtDesc)
  buckets.recent.sort(byRequestedAtDesc)
  buckets.archive.sort(byRequestedAtDesc)

  return buckets
}

export const daysUntilExpiry = (expiresAt: string | null | undefined, now: Date): number | null => {
  const at = toTime(expiresAt)
  if (at === null) return null
  return (at - now.getTime()) / DAY_MS
}

export const toExpiryTone = (expiresAt: string | null | undefined, now: Date): DownloadExpiryTone => {
  const days = daysUntilExpiry(expiresAt, now)
  if (days === null) return 'none'
  if (days <= 0) return 'expired'
  if (days <= DOWNLOAD_EXPIRY_URGENT_DAYS) return 'urgent'
  if (days <= DOWNLOAD_EXPIRY_SOON_DAYS) return 'soon'
  return 'normal'
}

const toRow = (run: ServiceRun, artefact: ServiceArtefact | null): DownloadRow => ({
  key: artefact ? `${run.id}:${artefact.format}` : run.id,
  run,
  artefact,
  name: run.name,
  requestedAt: run.requestedAt,
  status: run.status,
  trigger: run.trigger,
  format: artefact?.format ?? null,
  sizeBytes: artefact?.sizeBytes ?? null,
  rowCount: artefact?.rowCount ?? null,
  expiresAt: artefact?.expiresAt ?? null,
  truncated: artefact?.truncated ?? false,
  isReady: run.status === 'done' && artefact !== null && artefact.available
})

export const toDownloadRows = (runs: ServiceRun[] | null | undefined): DownloadRow[] =>
  (runs ?? [])
    .flatMap((run) => {
      const artefacts = run.artefacts ?? []
      return artefacts.length === 0 ? [toRow(run, null)] : artefacts.map((artefact) => toRow(run, artefact))
    })
    .sort(
      (left, right) =>
        right.requestedAt.localeCompare(left.requestedAt) || (left.format ?? '').localeCompare(right.format ?? '')
    )

export const canDownloadRow = (row: DownloadRow, now: Date): boolean =>
  row.isReady && toExpiryTone(row.expiresAt, now) !== 'expired'

export const listDownloadFormats = (rows: DownloadRow[]): string[] =>
  [...new Set(rows.map((row) => row.format).filter((format): format is string => format !== null))].sort()

const matchesExpiry = (row: DownloadRow, filter: DownloadExpiryFilter, now: Date): boolean => {
  if (filter.kind === 'all') return true
  const days = daysUntilExpiry(row.expiresAt, now)
  if (days === null) return false
  if (filter.kind === 'expired') return days <= 0
  return days > 0 && days <= filter.days
}

const matchesValue = (value: string, filter: string | null | undefined): boolean =>
  !filter || filter === ALL_DOWNLOADS_FILTER || filter === value

const searchDownloadRows = (rows: DownloadRow[], query: string): DownloadRow[] => {
  const trimmed = query.trim()
  if (trimmed.length === 0) return rows

  const finder = new Fzf(rows, {
    selector: (row: DownloadRow) => row.name,
    match: extendedMatch,
    casing: 'case-insensitive',
    normalize: true,
    tiebreakers: [byLengthAsc]
  })

  return finder.find(trimmed).map((result) => result.item)
}

export const filterDownloadRows = (rows: DownloadRow[], filters: DownloadRowFilters, now: Date): DownloadRow[] => {
  const expiry = filters.expiry ?? { kind: 'all' }
  const kept = rows.filter(
    (row) =>
      matchesValue(row.format ?? '', filters.format) &&
      matchesValue(row.status, filters.status) &&
      matchesValue(row.trigger, filters.trigger) &&
      matchesExpiry(row, expiry, now)
  )
  return searchDownloadRows(kept, filters.search ?? '')
}

export const parseExpiringParam = (value: unknown): number | null => {
  const raw = Array.isArray(value) ? value[0] : value
  if (raw === null || raw === undefined || raw === '') return null
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return null
  const days = Math.floor(parsed)
  return days > 0 ? days : null
}

export const DOWNLOADS_PAGE_SIZE = 25

export type DownloadSortColumn = 'name' | 'trigger' | 'status' | 'requested' | 'format' | 'size' | 'rows' | 'expiry'

export interface DownloadSort {
  columnId: string
  direction: 'asc' | 'desc'
}

export const DEFAULT_DOWNLOADS_SORT: DownloadSort = { columnId: 'requested', direction: 'desc' }

export type DownloadSourceKind = 'schedule' | 'definition'

export interface DownloadSource {
  kind: DownloadSourceKind
  to: string
}

export interface DownloadSummary {
  fileCount: number
  totalBytes: number
  expiringCount: number
  expiredCount: number
  runningCount: number
}

const STATUS_RANK: Record<ServiceRunStatus, number> = { running: 0, queued: 1, done: 2, failed: 3 }

const TRIGGER_RANK: Record<ServiceRunTrigger, number> = { schedule: 0, manual: 1, api: 2 }

type SortValue = string | number | null

const SORT_VALUES: Record<DownloadSortColumn, (row: DownloadRow) => SortValue> = {
  name: (row) => row.name.trim().toLowerCase() || null,
  trigger: (row) => TRIGGER_RANK[row.trigger] ?? Number.MAX_SAFE_INTEGER,
  status: (row) => STATUS_RANK[row.status] ?? Number.MAX_SAFE_INTEGER,
  requested: (row) => toTime(row.requestedAt),
  format: (row) => row.format,
  size: (row) => row.sizeBytes,
  rows: (row) => row.rowCount,
  expiry: (row) => toTime(row.expiresAt)
}

export const isDownloadSortColumn = (columnId: string): columnId is DownloadSortColumn => columnId in SORT_VALUES

const byNewestThenKey = (left: DownloadRow, right: DownloadRow): number =>
  right.requestedAt.localeCompare(left.requestedAt) || left.key.localeCompare(right.key)

const compareSortValues = (left: Exclude<SortValue, null>, right: Exclude<SortValue, null>): number =>
  typeof left === 'string' && typeof right === 'string' ? left.localeCompare(right) : Number(left) - Number(right)

export const sortDownloadRows = (rows: DownloadRow[], sort: DownloadSort): DownloadRow[] => {
  if (!isDownloadSortColumn(sort.columnId)) return [...rows]
  const read = SORT_VALUES[sort.columnId]
  const sign = sort.direction === 'desc' ? -1 : 1

  return [...rows].sort((left, right) => {
    const leftValue = read(left)
    const rightValue = read(right)
    if (leftValue === null && rightValue === null) return byNewestThenKey(left, right)
    if (leftValue === null) return 1
    if (rightValue === null) return -1
    const delta = compareSortValues(leftValue, rightValue)
    return delta === 0 ? byNewestThenKey(left, right) : delta * sign
  })
}

export const toPageCount = (total: number, perPage: number): number =>
  perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1

export const clampPage = (page: number, total: number, perPage: number): number => {
  const requested = Number.isFinite(page) ? Math.floor(page) : 1
  return Math.min(Math.max(1, requested), toPageCount(total, perPage))
}

export const pageOfRows = <T>(rows: T[], page: number, perPage: number): T[] => {
  if (perPage <= 0) return [...rows]
  const start = (clampPage(page, rows.length, perPage) - 1) * perPage
  return rows.slice(start, start + perPage)
}

export const toDownloadSource = (row: DownloadRow): DownloadSource | null => {
  if (row.run.scheduleId) return { kind: 'schedule', to: `/reports/scheduled/${row.run.scheduleId}` }
  if (row.run.definitionId) return { kind: 'definition', to: definitionRoute(row.run.definitionId) }
  return null
}

export const summariseDownloadRows = (
  rows: DownloadRow[],
  now: Date,
  windowDays: number = DOWNLOAD_EXPIRY_SOON_DAYS
): DownloadSummary => {
  const running = new Set<string>()
  const summary: DownloadSummary = {
    fileCount: 0,
    totalBytes: 0,
    expiringCount: 0,
    expiredCount: 0,
    runningCount: 0
  }

  for (const row of rows) {
    if (isDownloadRunActive(row.run)) running.add(row.run.id)
    if (row.artefact === null) continue
    summary.fileCount += 1
    summary.totalBytes += row.sizeBytes ?? 0
    const days = daysUntilExpiry(row.expiresAt, now)
    if (days === null) continue
    if (days <= 0) summary.expiredCount += 1
    else if (days <= windowDays) summary.expiringCount += 1
  }

  summary.runningCount = running.size
  return summary
}
